import React, { useEffect, useState } from 'react';
import { errorColor } from './common/ColorCodes';
import { Box, TextField, Typography, Card, Fade, Zoom, LinearProgress, Chip, Avatar, Stepper, Step, StepLabel } from '@mui/material';
import { Email, CheckCircle, Send, Security, Verified, ArrowForward, Refresh } from '@mui/icons-material';
import {
    CONST_BUSINESS_MAIL,
    CONST_SITE_LABEL,
    MESSAGES,
    STORAGE_KEYS,
    validateEmail
} from './common/Helper';
import './common/Styles.css';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';
import { Link, useSearchParams } from 'react-router-dom';
import DebugService from '../services/DebugService';
import { LoadingButton } from '@mui/lab';
import RegistrationApiService from '../services/RegistrationApiService';
import Copyright from './common/Copyright';
import { useAuth } from '../state/AuthContext';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';

/**
 * Resend Email Link Component
 * @returns 
 */
export const EmailInput = () => {
    const [formValues, setFormValues] = useState({ email: '' });
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [errors, setErrors] = useState({ email: '' });
    const [showSuccess, setShowSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    /**
     * On email input change handler
     * @param {*} e 
     */
    const handleChange = (e) => {
        setIsButtonDisabled(true)

        const { id, value } = e.target;
        setFormValues({
            ...formValues,
            [id]: value,
        });

        if (validateEmail(value)) {
            setIsButtonDisabled(false)
        }
    }

    /**
        * Submit button click handler
        * @param {*} e 
    */
    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true)

        if (!validateEmail(formValues.email)) {
            setIsLoading(false)
            setErrors({
                email: MESSAGES.EMAIL_VALID
            })
        } else {
            DebugService.log('Resend Email Component: Form is valid. Sending Link', formValues);

            const requestBody = {
                email: formValues['email']
            }

            RegistrationApiService.resendEmailLink(requestBody)
                .then(res => {
                    const data = res['data'] ?? {}
                    const error = res['error'] ?? {}
                    DebugService.log(`Resend Email Component: Data`, data)


                    if (error && error['message']) {
                        setErrors({
                            'email': error['message']
                        })
                    } else {
                        setErrors({})
                        setShowSuccess(true)
                    }

                    setIsLoading(false)
                })
                .catch(err => {
                    DebugService.error('Resend Email  Component: Error caught', formValues, err);
                })
                .finally(() => { })
        }
    }

    return (
        <Card className='verify-email-card verify-email-card-premium'>
            {/* Progress indicator */}
            <div className='verify-email-progress-container'>
                <LinearProgress 
                    variant={isLoading ? 'indeterminate' : 'determinate'} 
                    value={showSuccess ? 100 : 0}
                    className='verify-email-progress'
                />
            </div>
            
            {/* Animated icon with pulse effect */}
            <div className='verify-email-icon verify-email-icon-animated'>
                <Send className={isLoading ? 'verify-email-icon-pulse' : ''} />
            </div>
            
            {/* Header with status chip */}
            <Box className='verify-email-header-section'>
                <Typography className='verify-email-title' variant='h4'>
                    Resend Verification Email
                </Typography>
                <Chip 
                    icon={<Security />} 
                    label="Secure Process" 
                    className='verify-email-security-chip'
                    size="small"
                />
            </Box>
            
            <Typography className='verify-email-description' variant='body1'>
                Enter your email address and we'll send you a secure verification link with advanced encryption.
            </Typography>
            
            {/* Step indicator */}
            <Box className='verify-email-steps'>
                <Stepper activeStep={showSuccess ? 2 : isLoading ? 1 : 0} className='verify-email-stepper'>
                    <Step>
                        <StepLabel>Enter Email</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Sending</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Sent</StepLabel>
                    </Step>
                </Stepper>
            </Box>
            
            {showSuccess ? (
                <Fade in={showSuccess} timeout={800}>
                    <Box className='verify-email-success verify-email-success-premium'>
                        <Avatar className='verify-email-success-avatar'>
                            <CheckCircle />
                        </Avatar>
                        <Typography variant='h6' className='verify-email-success-title'>
                            Email Sent Successfully!
                        </Typography>
                        <Typography variant='body2' className='verify-email-success-message'>
                            {MESSAGES.VERIFY_EMAIL_LINK_SUCCESS}
                        </Typography>
                        <Box className='verify-email-success-actions'>
                            <Chip 
                                icon={<Email />} 
                                label="Check your inbox" 
                                className='verify-email-inbox-chip'
                                variant="outlined"
                            />
                        </Box>
                    </Box>
                </Fade>
            ) : (
                <Zoom in={!showSuccess} timeout={500}>
                    <Box className='verify-email-form verify-email-form-premium'>
                        <TextField
                            className='verify-email-input verify-email-input-premium'
                            id="email"
                            name='email'
                            label="Enter your email address"
                            variant="outlined"
                            type="email"
                            required
                            fullWidth
                            value={formValues.email}
                            onChange={handleChange}
                            error={Boolean(errors.email)}
                            helperText={errors.email}
                            InputProps={{
                                startAdornment: (
                                    <Email className='verify-email-input-icon' />
                                )
                            }}
                        />
                        <LoadingButton
                            onClick={handleSubmit}
                            loading={isLoading}
                            loadingPosition="end"
                            type='submit'
                            disabled={isButtonDisabled}
                            className='verify-email-button verify-email-button-premium'
                            startIcon={<Send />}
                            endIcon={<ArrowForward />}
                        >
                            {isLoading ? 'Sending Secure Email...' : 'Send Verification Email'}
                        </LoadingButton>
                    </Box>
                </Zoom>
            )}
        </Card>
    )
}

/**
 * Verify Email Component
 * @returns 
 */
const VerifyEmail = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [apiError, setAPIError] = useState('');
    const [showResendOption, setShowResendOption] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const { refetchUserDetails } = useAuth()

    const { getScopedItem } = useScopedLocalStorage()

    const resendLink = () => {
        setShowResendOption(true)
    }

    /**
     * Submit button click handler
     * @param {*} e 
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true)

        const requestBody = {
            token: searchParams.get('token') ?? '',
            userId: searchParams.get('userId') ?? ''
        }

        if (requestBody['token'] === '' || requestBody['userId'] === '') {
            DebugService.error('Verify Email Component: Invalid token or user id', requestBody);
        } else {
            RegistrationApiService.verifyEmail(requestBody)
                .then(res => {
                    const error = res['error'] ?? {}

                    DebugService.log(`Verify Email Component: Verify Response`, res)

                    if (error && error['message']) {

                        if (getScopedItem) {
                            refetchUserDetails()
                        }

                        setIsLoading(false)
                        setAPIError(error['message'])
                    } else {
                        if (getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)) {
                            refetchUserDetails()
                        }
                        setIsLoading(false)
                        setShowSuccess(true)
                    }
                })
                .catch(err => {
                    DebugService.error('Verify Email Component: API Error caught', requestBody, err);
                })
                .finally(() => { })
        }
    }

    useEffect(() => {
        document.title = `${CONST_SITE_LABEL} | Verify Email`
    }, [])

    return (
        <div className='verify-email-container verify-email-container-premium'>
            {/* Animated background particles */}
            <div className='verify-email-particles'>
                <div className='verify-email-particle verify-email-particle-1'></div>
                <div className='verify-email-particle verify-email-particle-2'></div>
                <div className='verify-email-particle verify-email-particle-3'></div>
            </div>
            
            {/* Premium Header */}
            <div className='verify-email-header verify-email-header-premium'>
                <Box className='verify-email-brand'>
                    <Avatar className='verify-email-brand-avatar'>
                        <Verified />
                    </Avatar>
                    <Link 
                        title='Home' 
                        to="/" 
                        className='verify-email-logo verify-email-logo-premium'
                    >
                        {CONST_SITE_LABEL}
                    </Link>
                </Box>
                <Chip 
                    icon={<Security />}
                    label={CONST_BUSINESS_MAIL}
                    component="a"
                    href={`mailto:${CONST_BUSINESS_MAIL}`}
                    clickable
                    className='verify-email-support verify-email-support-premium'
                />
            </div>

            {/* Main Content with enhanced animations */}
            <div className='verify-email-main verify-email-main-premium'>
                {showResendOption ? (
                    <Fade in={showResendOption} timeout={600}>
                        <div>
                            <EmailInput />
                        </div>
                    </Fade>
                ) : (
                    <Card className='verify-email-card verify-email-card-premium'>
                        {/* Progress indicator */}
                        <div className='verify-email-progress-container'>
                            <LinearProgress 
                                variant={isLoading ? 'indeterminate' : 'determinate'} 
                                value={showSuccess ? 100 : 0}
                                className='verify-email-progress'
                            />
                        </div>
                        
                        {/* Animated icon with glow effect */}
                        <div className='verify-email-icon verify-email-icon-premium'>
                            <Email className={isLoading ? 'verify-email-icon-pulse' : ''} />
                            <div className='verify-email-icon-glow'></div>
                        </div>
                        
                        {/* Header with status */}
                        <Box className='verify-email-header-section'>
                            <Typography className='verify-email-title verify-email-title-premium' variant='h4'>
                                Verify Email Address
                            </Typography>
                            <Chip 
                                icon={<Security />} 
                                label="Secure Verification" 
                                className='verify-email-security-chip'
                                size="small"
                            />
                        </Box>
                        
                        <Typography className='verify-email-description verify-email-description-premium' variant='body1'>
                            Complete your account verification with our secure, one-click email verification process.
                        </Typography>
                        
                        {/* Enhanced step indicator */}
                        <Box className='verify-email-steps'>
                            <Stepper activeStep={showSuccess ? 2 : isLoading ? 1 : 0} className='verify-email-stepper'>
                                <Step>
                                    <StepLabel>Click Verify</StepLabel>
                                </Step>
                                <Step>
                                    <StepLabel>Processing</StepLabel>
                                </Step>
                                <Step>
                                    <StepLabel>Verified</StepLabel>
                                </Step>
                            </Stepper>
                        </Box>
                        
                        {showSuccess ? (
                            <Fade in={showSuccess} timeout={800}>
                                <Box className='verify-email-success verify-email-success-premium'>
                                    <Avatar className='verify-email-success-avatar'>
                                        <CheckCircle />
                                    </Avatar>
                                    <Typography variant='h5' className='verify-email-success-title'>
                                        Email Verified Successfully!
                                    </Typography>
                                    <Typography variant='body1' className='verify-email-success-message'>
                                        {MESSAGES.VERIFY_EMAIL_SUCCESS}
                                    </Typography>
                                    <Box className='verify-email-success-actions'>
                                        <LoadingButton
                                            component={Link}
                                            to={CONST_ROUTE_PATH.DASHBOARD}
                                            className='verify-email-dashboard-button'
                                            startIcon={<ArrowForward />}
                                            variant="contained"
                                        >
                                            Continue to Dashboard
                                        </LoadingButton>
                                    </Box>
                                </Box>
                            </Fade>
                        ) : (
                            <Zoom in={!showSuccess} timeout={500}>
                                <Box className='verify-email-form verify-email-form-premium'>
                                    <LoadingButton
                                        onClick={handleSubmit}
                                        loading={isLoading}
                                        loadingPosition="end"
                                        type='submit'
                                        className='verify-email-button verify-email-button-premium'
                                        startIcon={<Verified />}
                                        endIcon={<ArrowForward />}
                                        size="large"
                                    >
                                        {isLoading ? 'Verifying Your Email...' : 'Verify Email Address'}
                                    </LoadingButton>

                                    {/* Enhanced error display */}
                                    {apiError && (
                                        <Fade in={!!apiError} timeout={400}>
                                            <Box className='verify-email-error verify-email-error-premium'>
                                                <Typography variant='body2'>
                                                    {apiError}
                                                </Typography>
                                            </Box>
                                        </Fade>
                                    )}

                                    {/* Premium resend section */}
                                    <Box className='verify-email-resend-section'>
                                        <Typography variant='body2' className='verify-email-resend-text'>
                                            Didn't receive the email?
                                        </Typography>
                                        <LoadingButton
                                            className='verify-email-resend-link verify-email-resend-link-premium'
                                            onClick={resendLink}
                                            startIcon={<Refresh />}
                                            variant="text"
                                            size="small"
                                        >
                                            Resend verification link
                                        </LoadingButton>
                                    </Box>
                                </Box>
                            </Zoom>
                        )}
                    </Card>
                )}
            </div>

            {/* Enhanced Footer */}
            <div className='verify-email-footer verify-email-footer-premium'>
                <Box className='verify-email-footer-content'>
                    <Copyright />
                    <Chip 
                        icon={<Security />}
                        label="256-bit SSL Encrypted"
                        size="small"
                        className='verify-email-ssl-chip'
                    />
                </Box>
            </div>
        </div>
    )
}

export default VerifyEmail